/**
 * Logging levels in order of verbosity
 */
const LogLevels = {
	DEBUG: 0,
	INFO: 1,
	WARN: 2,
	ERROR: 3,
	NONE: 4,
};

/**
 * Gets the current log level from environment variables
 * @returns {number} The numeric log level
 */
const getLogLevel = () => {
	const envLevel = process.env.REACT_APP_LOG_LEVEL || "INFO";
	const level = envLevel.toUpperCase();

	if (Object.prototype.hasOwnProperty.call(LogLevels, level)) {
		return LogLevels[level];
	}

	// Default to INFO if the specified level is invalid
	return LogLevels.INFO;
};

/**
 * Logger class for consistent logging throughout the application
 */
class Logger {
	constructor(context) {
		this.context = context || "";
		this.logLevel = getLogLevel();
	}

	/**
	 * Format the log message with a timestamp and context
	 * @param {string} level - The log level
	 * @param {string} message - The log message
	 * @param {object} [data] - Optional data to include in the log
	 * @returns {Array} Formatted log arguments
	 */
	formatLog(level, message, data) {
		const timestamp = new Date().toISOString();
		const prefix = `[${timestamp}] [${level}]${this.context ? ` [${this.context}]` : ""}:`;

		if (data !== undefined) {
			return [prefix, message, data];
		}
		return [prefix, message];
	}

	/**
	 * Debug level logging
	 * @param {string} message - The message to log
	 * @param {object} [data] - Optional data to include
	 */
	debug(message, data) {
		if (this.logLevel <= LogLevels.DEBUG) {
			console.log(...this.formatLog("DEBUG", message, data));
		}
	}

	/**
	 * Info level logging
	 * @param {string} message - The message to log
	 * @param {object} [data] - Optional data to include
	 */
	info(message, data) {
		if (this.logLevel <= LogLevels.INFO) {
			console.info(...this.formatLog("INFO", message, data));
		}
	}

	/**
	 * Warning level logging
	 * @param {string} message - The message to log
	 * @param {object} [data] - Optional data to include
	 */
	warn(message, data) {
		if (this.logLevel <= LogLevels.WARN) {
			console.warn(...this.formatLog("WARN", message, data));
		}
	}

	/**
	 * Error level logging
	 * @param {string} message - The message to log
	 * @param {object} [data] - Optional data to include
	 */
	error(message, data) {
		if (this.logLevel <= LogLevels.ERROR) {
			console.error(...this.formatLog("ERROR", message, data));
		}
	}

	/**
	 * Creates a child logger with a specified context
	 * @param {string} context - The context for the child logger
	 * @returns {Logger} A new logger with the combined context
	 */
	child(context) {
		const combinedContext = this.context
			? `${this.context}.${context}`
			: context;
		return new Logger(combinedContext);
	}
}

/**
 * Create a logger with the specified context
 * @param {string} [context] - Optional context for the logger
 * @returns {Logger} A new Logger instance
 */
const createLogger = (context = "") => {
	return new Logger(context);
};

// Create a default root logger
const logger = createLogger();

export { createLogger, LogLevels };
export default logger;
